import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, CanActivate } from "@angular/router";
import { Store } from "@ngrx/store";
import { AlertButtonColor, AlertButtonStyle } from "@obg/mat.ui.core";
import {
	AbstractTranslatableAlertService,
	AllowedAction,
	CORE_ROUTE,
	CustomerManagementSelector,
	KycState,
} from "@obg/ngx.core";
import { TranslationGroup } from "@obg/ngx.core.content";
import { PlatformService } from "@obg/ngx.global.evo";
import { RouteAction } from "@obg/ngx.router";
import { Observable, of } from "rxjs";
import { switchMap, take } from "rxjs/operators";

import { CommonState } from "../state";

import { CompleteVerificationConfigService } from "./complete-verification.config";

const COMPLETE_VERIFICATION_ID = "complete-verification";

@Injectable({ providedIn: "root" })
export class CompleteVerificationGuard implements CanActivate {
	constructor(
		private depositVerifyNotificationConfigService: CompleteVerificationConfigService,
		private store: Store<CommonState>,
		private alertService: AbstractTranslatableAlertService,
		private platformService: PlatformService
	) {}

	canActivate(route: ActivatedRouteSnapshot) {
		const actionType = route.data.actionType as keyof AllowedAction;

		if (
			!actionType ||
			this.platformService.isServer ||
			!this.depositVerifyNotificationConfigService.showVerifyNotification()
		) {
			return of(true);
		}

		return this.store.select(CustomerManagementSelector.getCustomer).pipe(
			take(1),
			switchMap((customer) => {
				if (
					customer?.kyc?.state === KycState.Accepted ||
					customer?.allowedActions?.[actionType]?.isAllowed === false
				) {
					return of(true);
				}

				return this.showVerifyNotification$(actionType);
			})
		);
	}

	showVerifyNotification$(actionType: keyof AllowedAction): Observable<boolean> {
		return this.alertService
			.open$(
				{
					dialogConfig: {
						disableClose: true,
						maxWidth: "none",
					},
					buttons: [
						{
							color: AlertButtonColor.Accent,
							style: AlertButtonStyle.Flat,
							text: "kyc-verify-popup.upload-documents",
							handler: () => {
								this.store.dispatch(RouteAction.navigate(CORE_ROUTE.verification.root));
								return false;
							},
						},
						{
							color: AlertButtonColor.Primary,
							style: AlertButtonStyle.Stroked,
							text: `kyc-verify-popup.complete-verification.continue-${actionType}`,
							handler: () => true,
						},
					],
					hideClose: true,
					variant: "alternate",
					verticalButtons: true,
					id: COMPLETE_VERIFICATION_ID,
					testId: COMPLETE_VERIFICATION_ID,
					title: "kyc-verify-popup.complete-verification.title",
					message: `kyc-verify-popup.complete-verification.message`,
				},
				[TranslationGroup.General]
			)
			.pipe(switchMap((dialogRef) => dialogRef.afterClosed()));
	}
}
